/**
 * PR Action Constants
 *
 * Defines balance constants for PR (Public Relations) actions.
 * PR actions shape public perception, manage scandals, and amplify successes.
 *
 * @module constants/pr
 */

// ============================================================================
// Types
// ============================================================================

export type PRSubtype = 'general_messaging' | 'damage_control' | 'highlight_success'
export type PRFocus = 'balanced' | 'regional'
export type DamageControlStrategy = 'apologize' | 'deny' | 'pivot'
export type AmplificationScope = 'national' | 'targeted'

/**
 * Configuration for a PR subtype.
 */
export interface PRSubtypeConfig {
  key: PRSubtype
  label: string
  description: string
  icon: string
  detailedDescription: string
}

/**
 * Configuration for a damage control strategy.
 */
export interface DamageControlStrategyConfig {
  key: DamageControlStrategy
  label: string
  description: string
  riskLevel: 'low' | 'medium' | 'high'
  riskDescription: string
}

/**
 * Configuration for a message theme.
 */
export interface MessageThemeConfig {
  key: string
  label: string
  description: string
}

// ============================================================================
// PR Subtype Configurations
// ============================================================================

export const PR_SUBTYPES: Record<PRSubtype, PRSubtypeConfig> = {
  general_messaging: {
    key: 'general_messaging',
    label: 'General Messaging',
    description: 'Broad communications to build recognition and public favorability',
    icon: '📣',
    detailedDescription:
      'Increases party recognition across all districts with a small boost to momentum. Good baseline action when no specific crisis or success to address.',
  },
  damage_control: {
    key: 'damage_control',
    label: 'Damage Control',
    description: 'Respond to scandals or negative coverage to limit reputational damage',
    icon: '🛡️',
    detailedDescription:
      'Reduces the ongoing impact of active scandals and can speed up scandal decay. More effective when used promptly after a scandal breaks.',
  },
  highlight_success: {
    key: 'highlight_success',
    label: 'Highlight Success',
    description: 'Amplify recent achievements to build momentum and recognition',
    icon: '🏆',
    detailedDescription:
      'Converts policy successes or campaign wins into lasting momentum. Boosts recognition and favorability in areas where success occurred.',
  },
} as const

// ============================================================================
// Damage Control Strategies
// ============================================================================

export const DAMAGE_CONTROL_STRATEGIES: Record<DamageControlStrategy, DamageControlStrategyConfig> = {
  apologize: {
    key: 'apologize',
    label: 'Apologize & Reform',
    description: 'Accept responsibility and promise changes',
    riskLevel: 'low',
    riskDescription: 'Safe choice with reliable scandal decay, plus small loyalty boost with affected cohorts.',
  },
  deny: {
    key: 'deny',
    label: 'Deny & Deflect',
    description: 'Challenge the accusations and shift blame',
    riskLevel: 'high',
    riskDescription: 'High risk/reward: chance to significantly reduce scandal impact OR backfire and extend it.',
  },
  pivot: {
    key: 'pivot',
    label: 'Pivot & Redirect',
    description: 'Acknowledge briefly and change the subject',
    riskLevel: 'medium',
    riskDescription: 'Moderate scandal decay while shifting media attention to other topics.',
  },
} as const

export const DAMAGE_CONTROL_STRATEGY_ORDER: DamageControlStrategy[] = ['apologize', 'deny', 'pivot']

// ============================================================================
// Message Themes (for General Messaging)
// ============================================================================

export const MESSAGE_THEMES: MessageThemeConfig[] = [
  {
    key: 'party_unity',
    label: 'Party Unity',
    description: 'Emphasize internal cohesion and shared purpose',
  },
  {
    key: 'leadership_strength',
    label: 'Leadership Strength',
    description: 'Highlight capable leadership and decisive action',
  },
  {
    key: 'policy_vision',
    label: 'Policy Vision',
    description: 'Focus on policy goals and ideological principles',
  },
  {
    key: 'community_connection',
    label: 'Community Connection',
    description: 'Emphasize grassroots support and local engagement',
  },
] as const

// ============================================================================
// PR Effect Constants
// ============================================================================

export const PR_EFFECTS = {
  // -------------------------------------------------------------------------
  // General Messaging Effects
  // -------------------------------------------------------------------------

  /**
   * Base recognition boost per AP spent for general messaging.
   * @tuning Increase for more impactful general PR
   */
  GENERAL_RECOGNITION_PER_AP: 2,

  /**
   * Base momentum boost per AP spent for general messaging.
   * @tuning Increase for more momentum from general PR
   */
  GENERAL_MOMENTUM_PER_AP: 1,

  /**
   * Efficiency multiplier for regional focus.
   * Regional focus concentrates effect in selected region.
   * @tuning Increase to make regional focus more powerful
   */
  REGIONAL_FOCUS_MULTIPLIER: 1.5,

  /**
   * Efficiency multiplier for balanced/national focus.
   * Balanced focus spreads effect across all districts with reduced per-district impact.
   * @tuning Decrease to make balanced focus weaker per district
   */
  BALANCED_FOCUS_EFFICIENCY: 0.8,

  /**
   * Spillover effect for districts outside the regional focus.
   * Regional campaigns have small impact elsewhere.
   * @tuning Increase to make regional campaigns leak more to other districts
   */
  REGIONAL_SPILLOVER: 0.3,

  /**
   * Momentum multiplier when party already has positive momentum (>10).
   * Represents "riding the wave" of positive coverage.
   * @tuning Increase to make momentum snowball faster
   */
  POSITIVE_MOMENTUM_BONUS: 1.2,

  /**
   * Momentum penalty when party has active scandals.
   * Harder to get positive coverage when embroiled in controversy.
   * @tuning Decrease to make scandals hurt PR more
   */
  SCANDAL_PENALTY: 0.7,

  // -------------------------------------------------------------------------
  // Damage Control Effects
  // -------------------------------------------------------------------------

  /**
   * Base scandal impact reduction at 1 AP (as decimal: 0.20 = 20%).
   * @tuning Increase for more effective damage control
   */
  DAMAGE_CONTROL_BASE_MITIGATION: 0.20,

  /**
   * Additional mitigation per AP spent (0.15 = 15% per AP).
   * At 1 AP: 35%, at 2 AP: 50%, at 3 AP: 65%
   * @tuning Increase to reward higher AP investment
   */
  DAMAGE_CONTROL_MITIGATION_PER_AP: 0.15,

  /**
   * Base scandal impact reduction percentage at 1 AP (legacy, for UI display).
   * @tuning Increase for more effective damage control
   */
  DAMAGE_CONTROL_BASE_REDUCTION: 20,

  /**
   * Additional reduction per AP spent beyond first (legacy, for UI display).
   * @tuning Increase to reward higher AP investment
   */
  DAMAGE_CONTROL_REDUCTION_PER_AP: 15,

  /**
   * Base decay acceleration in ticks per AP spent.
   * @tuning Increase to make scandals resolve faster with PR
   */
  DAMAGE_CONTROL_BASE_DECAY_ACCELERATION: 1,

  /**
   * Decay acceleration in ticks per AP spent.
   * @tuning Increase to make scandals resolve faster with PR
   */
  DAMAGE_CONTROL_DECAY_PER_AP: 1,

  /**
   * Multiplier for 'apologize' strategy effectiveness.
   * Reliable but moderate effect.
   */
  STRATEGY_APOLOGIZE_MULTIPLIER: 1.0,

  /**
   * Extra decay bonus for 'apologize' strategy (ticks).
   */
  STRATEGY_APOLOGIZE_DECAY_BONUS: 1,

  /**
   * Loyalty boost per AP for 'apologize' strategy with affected cohorts.
   */
  STRATEGY_APOLOGIZE_LOYALTY_PER_AP: 1,

  /**
   * Loyalty boost for 'apologize' strategy with affected cohorts (legacy for UI).
   */
  STRATEGY_APOLOGIZE_LOYALTY_BOOST: 2,

  /**
   * Multiplier for 'deny' strategy on success.
   * High risk, high reward.
   */
  STRATEGY_DENY_SUCCESS_MULTIPLIER: 1.8,

  /**
   * Base backfire chance for 'deny' strategy (0.30 = 30%).
   * @tuning Increase to make deny riskier
   */
  STRATEGY_DENY_BASE_BACKFIRE_RISK: 0.30,

  /**
   * Additional backfire risk for major scandals (0.20 = +20%).
   */
  STRATEGY_DENY_MAJOR_SCANDAL_BACKFIRE_BONUS: 0.20,

  /**
   * Impact increase when deny backfires (0.20 = +20% impact).
   */
  STRATEGY_DENY_BACKFIRE_IMPACT_INCREASE: 0.20,

  /**
   * Decay penalty when deny backfires (ticks added to duration).
   */
  STRATEGY_DENY_BACKFIRE_DECAY_PENALTY: 2,

  /**
   * Loyalty penalty when deny backfires.
   */
  STRATEGY_DENY_BACKFIRE_LOYALTY_PENALTY: -5,

  /**
   * Penalty multiplier for 'deny' strategy on failure (legacy).
   * Backfire extends scandal.
   */
  STRATEGY_DENY_FAILURE_MULTIPLIER: -0.5,

  /**
   * Chance of 'deny' strategy backfiring (legacy for UI, base 40%).
   * @tuning Increase to make deny riskier
   */
  STRATEGY_DENY_BACKFIRE_CHANCE: 0.40,

  /**
   * Multiplier for 'pivot' strategy effectiveness.
   * Moderate effect, shifts attention.
   */
  STRATEGY_PIVOT_MULTIPLIER: 0.8,

  /**
   * Media attention redirect bonus for 'pivot' strategy.
   * Reduces future scandal coverage / salience.
   */
  STRATEGY_PIVOT_ATTENTION_REDIRECT: 0.3,

  // -------------------------------------------------------------------------
  // Highlight Success Effects
  // -------------------------------------------------------------------------

  /**
   * Base recognition boost per AP spent for highlighting success.
   * Higher than general messaging (3 vs 2) since amplifying real achievement.
   * @tuning Increase to make highlighting more valuable
   */
  HIGHLIGHT_RECOGNITION_PER_AP: 3,

  /**
   * Base momentum boost per AP spent for highlighting success.
   * @tuning Increase to give more momentum from successes
   */
  HIGHLIGHT_MOMENTUM_PER_AP: 2,

  /**
   * Recognition boost multiplier for highlighting success (legacy for UI).
   * Higher than general messaging since amplifying real achievement.
   * @tuning Increase to make highlighting more valuable
   */
  HIGHLIGHT_RECOGNITION_MULTIPLIER: 1.5,

  /**
   * Momentum boost multiplier for highlighting success (legacy for UI).
   * @tuning Increase to give more momentum from successes
   */
  HIGHLIGHT_MOMENTUM_MULTIPLIER: 2,

  /**
   * Efficiency for national scope coverage.
   * National spreads effect across all districts with slight reduction.
   */
  HIGHLIGHT_NATIONAL_EFFICIENCY: 0.9,

  /**
   * Concentration bonus for targeted scope in success regions.
   * Targeted campaigns have much higher impact where success occurred.
   */
  HIGHLIGHT_TARGETED_CONCENTRATION: 1.8,

  /**
   * Spillover effect for districts outside targeted regions.
   */
  HIGHLIGHT_TARGETED_SPILLOVER: 0.4,

  /**
   * Bonus effect when targeting regions where success occurred (legacy).
   */
  HIGHLIGHT_TARGETED_BONUS: 1.3,

  /**
   * Bonus multiplier for fresh successes (within 2 ticks).
   * Fresh news is more compelling.
   * @tuning Increase to reward quick PR response
   */
  HIGHLIGHT_FRESH_SUCCESS_BONUS: 1.3,

  /**
   * Penalty multiplier for stale successes (4+ ticks old).
   * Old news is less compelling.
   * @tuning Decrease to punish delayed PR response more
   */
  HIGHLIGHT_STALE_SUCCESS_PENALTY: 0.7,

  /**
   * Tick threshold for "fresh" success bonus.
   */
  HIGHLIGHT_FRESH_THRESHOLD_TICKS: 2,

  /**
   * Tick threshold for "stale" success penalty.
   */
  HIGHLIGHT_STALE_THRESHOLD_TICKS: 4,

  /**
   * Diminishing returns factor per previous highlight.
   * e.g., 0.6 means second highlight is 60% as effective, third is 36%, etc.
   * @tuning Decrease to make repeat highlights less effective
   */
  HIGHLIGHT_REPEAT_DIMINISHING_FACTOR: 0.6,

  /**
   * Loyalty boost per AP for cohorts that benefited from the success.
   */
  HIGHLIGHT_LOYALTY_PER_AP: 1,

  // -------------------------------------------------------------------------
  // General PR Constants
  // -------------------------------------------------------------------------

  /**
   * Minimum AP cost for PR actions.
   */
  MIN_AP: 1,

  /**
   * Maximum AP cost for PR actions.
   */
  MAX_AP: 3,

  /**
   * Cooldown in ticks before same scandal can be addressed again.
   * Prevents spam PR on same issue.
   */
  DAMAGE_CONTROL_COOLDOWN_TICKS: 2,

  /**
   * Maximum ticks back to look for highlightable successes.
   */
  SUCCESS_LOOKBACK_TICKS: 4,

  /**
   * Maximum ticks back to look for addressable scandals.
   */
  SCANDAL_LOOKBACK_TICKS: 3,

  // -------------------------------------------------------------------------
  // Global Stat Limits
  // -------------------------------------------------------------------------

  /**
   * Minimum momentum value.
   */
  MOMENTUM_MIN: -100,

  /**
   * Maximum momentum value.
   */
  MOMENTUM_MAX: 100,

  /**
   * Minimum recognition value.
   */
  RECOGNITION_MIN: 0,

  /**
   * Maximum recognition value.
   */
  RECOGNITION_MAX: 100,

  /**
   * Momentum threshold for positive momentum bonus.
   */
  POSITIVE_MOMENTUM_THRESHOLD: 10,
} as const

// ============================================================================
// Event Type Multipliers for Highlight Success
// ============================================================================

/**
 * Multipliers for different event types when highlighting success.
 * Some successes are more PR-worthy than others.
 */
export const HIGHLIGHT_EVENT_MULTIPLIERS: Record<string, number> = {
  election_victory: 1.5,
  policy_success: 1.2,
  economic_improvement: 1.3,
  legislative_win: 1.2,
  coalition_formed: 1.1,
  endorsement_received: 1.0,
  default: 1.0,
} as const

/**
 * Get the event multiplier for a given event type.
 */
export function getEventMultiplier(eventType: string): number {
  return HIGHLIGHT_EVENT_MULTIPLIERS[eventType] ?? HIGHLIGHT_EVENT_MULTIPLIERS.default
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the configuration for a PR subtype.
 */
export function getPRSubtypeConfig(subtype: PRSubtype): PRSubtypeConfig {
  return PR_SUBTYPES[subtype]
}

/**
 * Get the configuration for a damage control strategy.
 */
export function getDamageControlStrategyConfig(strategy: DamageControlStrategy): DamageControlStrategyConfig {
  return DAMAGE_CONTROL_STRATEGIES[strategy]
}

/**
 * Calculate estimated effects for general messaging.
 */
export function calculateGeneralMessagingEffects(
  intensity: number,
  isRegional: boolean
): { recognition: number; momentum: number } {
  const focusMultiplier = isRegional ? PR_EFFECTS.REGIONAL_FOCUS_MULTIPLIER : 1
  return {
    recognition: Math.round(intensity * PR_EFFECTS.GENERAL_RECOGNITION_PER_AP * focusMultiplier),
    momentum: intensity * PR_EFFECTS.GENERAL_MOMENTUM_PER_AP,
  }
}

/**
 * Calculate estimated effects for damage control.
 */
export function calculateDamageControlEffects(
  intensity: number,
  strategy: DamageControlStrategy
): { impactReduction: number; decayAcceleration: number; riskNote: string } {
  const baseReduction = PR_EFFECTS.DAMAGE_CONTROL_BASE_REDUCTION +
    (intensity - 1) * PR_EFFECTS.DAMAGE_CONTROL_REDUCTION_PER_AP

  let effectiveReduction = baseReduction
  let riskNote = ''

  switch (strategy) {
    case 'apologize':
      effectiveReduction *= PR_EFFECTS.STRATEGY_APOLOGIZE_MULTIPLIER
      riskNote = `+${PR_EFFECTS.STRATEGY_APOLOGIZE_LOYALTY_BOOST} loyalty with affected voters`
      break
    case 'deny': {
      // Show the potential range
      const successReduction = Math.round(baseReduction * PR_EFFECTS.STRATEGY_DENY_SUCCESS_MULTIPLIER)
      effectiveReduction = successReduction
      riskNote = `${Math.round(PR_EFFECTS.STRATEGY_DENY_BACKFIRE_CHANCE * 100)}% chance to backfire`
      break
    }
    case 'pivot':
      effectiveReduction *= PR_EFFECTS.STRATEGY_PIVOT_MULTIPLIER
      riskNote = 'Shifts media attention away from scandal'
      break
  }

  return {
    impactReduction: Math.round(effectiveReduction),
    decayAcceleration: intensity * PR_EFFECTS.DAMAGE_CONTROL_DECAY_PER_AP,
    riskNote,
  }
}

/**
 * Calculate estimated effects for highlighting success.
 */
export function calculateHighlightSuccessEffects(
  intensity: number,
  isNational: boolean
): { recognition: number; momentum: number } {
  const baseRecognition = intensity * PR_EFFECTS.GENERAL_RECOGNITION_PER_AP * PR_EFFECTS.HIGHLIGHT_RECOGNITION_MULTIPLIER
  const baseMomentum = intensity * PR_EFFECTS.GENERAL_MOMENTUM_PER_AP * PR_EFFECTS.HIGHLIGHT_MOMENTUM_MULTIPLIER

  const scopeMultiplier = isNational
    ? PR_EFFECTS.HIGHLIGHT_NATIONAL_EFFICIENCY
    : PR_EFFECTS.HIGHLIGHT_TARGETED_BONUS

  return {
    recognition: Math.round(baseRecognition * scopeMultiplier),
    momentum: Math.round(baseMomentum * scopeMultiplier),
  }
}
